import type { DataEntryUser } from '../types/dataEntryUser';
import type { Store } from '../types/store';
import type { WarehouseGoodsItem } from '../types/warehouseGoodsCatalog';
import {
  getCukcukMappingStatus,
  normalizeCukcukValue,
  type CukcukMappingStatus,
} from '../utils/cukcukMapping';
import { storeService } from './storeService';
import { warehouseGoodsCatalogService } from './warehouseGoodsCatalogService';

export type CukcukMappingActor = Pick<DataEntryUser, 'isAdmin'>;

export interface CukcukMaterialMapping {
  id: string;
  name: string;
  internalCode: string;
  cukcukMaterialCode: string | null;
  cukcukUnit: string | null;
  status: CukcukMappingStatus;
}

export interface CukcukStoreMapping {
  id: string;
  name: string;
  cukcukWarehouseCode: string | null;
  status: CukcukMappingStatus;
}

export interface CukcukMappingSummaryPart {
  total: number;
  mapped: number;
  unmapped: number;
}

export interface CukcukMappingSummary {
  materials: CukcukMappingSummaryPart;
  stores: CukcukMappingSummaryPart;
}

export class CukcukMappingPermissionError extends Error {
  readonly code = 'CUKCUK_MAPPING_FORBIDDEN';

  constructor() {
    super('Only an administrator can update CUKCUK mappings');
    this.name = 'CukcukMappingPermissionError';
  }
}

const assertCanUpdateMapping = (actor?: CukcukMappingActor): void => {
  if (!actor?.isAdmin) throw new CukcukMappingPermissionError();
};

const requireId = (value: string, label: string): string => {
  const id = value.trim();
  if (!id) throw new Error(`${label} is required`);
  return id;
};

const toMaterialMapping = (item: WarehouseGoodsItem): CukcukMaterialMapping => {
  const cukcukMaterialCode = normalizeCukcukValue(item.cukcukMaterialCode);
  const cukcukUnit = normalizeCukcukValue(item.cukcukUnit);

  return {
    id: item.id || '',
    name: item.name,
    internalCode: item.productCode,
    cukcukMaterialCode,
    cukcukUnit,
    status: getCukcukMappingStatus({ cukcukMaterialCode, cukcukUnit }),
  };
};

const toStoreMapping = (store: Store): CukcukStoreMapping => {
  const cukcukWarehouseCode = normalizeCukcukValue(store.cukcukWarehouseCode);

  return {
    id: store.id || '',
    name: store.name,
    cukcukWarehouseCode,
    status: getCukcukMappingStatus({ cukcukWarehouseCode }),
  };
};

const getSummaryPart = (mappedValues: boolean[]): CukcukMappingSummaryPart => {
  const mapped = mappedValues.filter(Boolean).length;
  return {
    total: mappedValues.length,
    mapped,
    unmapped: mappedValues.length - mapped,
  };
};

export const cukcukMappingService = {
  async getMaterialMappings(): Promise<CukcukMaterialMapping[]> {
    const materials = await warehouseGoodsCatalogService.getAll();
    return materials.map(toMaterialMapping);
  },

  async getStoreMappings(): Promise<CukcukStoreMapping[]> {
    const stores = await storeService.getStores();
    return stores.map(toStoreMapping);
  },

  async getSummary(): Promise<CukcukMappingSummary> {
    const [materials, stores] = await Promise.all([
      warehouseGoodsCatalogService.getAll(),
      storeService.getStores(),
    ]);

    return {
      materials: getSummaryPart(
        materials.map(
          (item) =>
            normalizeCukcukValue(item.cukcukMaterialCode) !== null &&
            normalizeCukcukValue(item.cukcukUnit) !== null,
        ),
      ),
      stores: getSummaryPart(
        stores.map((store) => normalizeCukcukValue(store.cukcukWarehouseCode) !== null),
      ),
    };
  },

  async updateMaterialMapping(
    materialId: string,
    mapping: { cukcukMaterialCode?: unknown; cukcukUnit?: unknown },
    actor?: CukcukMappingActor,
  ): Promise<void> {
    assertCanUpdateMapping(actor);
    const normalized: { cukcukMaterialCode?: string | null; cukcukUnit?: string | null } = {};

    if (Object.prototype.hasOwnProperty.call(mapping, 'cukcukMaterialCode')) {
      normalized.cukcukMaterialCode = normalizeCukcukValue(mapping.cukcukMaterialCode);
    }
    if (Object.prototype.hasOwnProperty.call(mapping, 'cukcukUnit')) {
      normalized.cukcukUnit = normalizeCukcukValue(mapping.cukcukUnit);
    }
    if (Object.keys(normalized).length === 0) {
      throw new Error('At least one CUKCUK material mapping field is required');
    }

    await warehouseGoodsCatalogService.updateCukcukMapping(
      requireId(materialId, 'Material id'),
      normalized,
    );
  },

  async updateStoreMapping(
    storeId: string,
    mapping: { cukcukWarehouseCode?: unknown },
    actor?: CukcukMappingActor,
  ): Promise<void> {
    assertCanUpdateMapping(actor);
    if (!Object.prototype.hasOwnProperty.call(mapping, 'cukcukWarehouseCode')) {
      throw new Error('CUKCUK warehouse code is required');
    }

    await storeService.updateCukcukWarehouseCode(
      requireId(storeId, 'Store id'),
      normalizeCukcukValue(mapping.cukcukWarehouseCode),
    );
  },
};
